import { defineStore } from 'pinia';
import { type Ref, ref, watch } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface Notification {
  id?: string | number;
  title: string;
  message: string;
  extra?: string;
  type?: 'success' | 'error' | 'info' | 'warning' | 'primary' | 'secondary' | 'neutral';
  image?: string;
  imageAlt?: string;
  progress?: number | null;
  timeout?: number | null;
  actions?: {
    icon?: IconDefinition;
    text: string;
    onClick: () => void;
  }[];
  onDismiss?: () => void;
}

interface ActiveNotification extends Notification {
  open: boolean;
}

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<Ref<ActiveNotification>[]>([]);

  const addNotification = (notification: Notification) => {
    const activeNotification = ref<ActiveNotification>({ ...notification, open: true });
    notifications.value.push(activeNotification);

    watch(
      () => activeNotification.value.open,
      (open) => {
        if (!open) {
          const index = notifications.value.indexOf(activeNotification);
          notifications.value.splice(index, 1);
          notification.onDismiss?.();
        }
      },
    );
  };

  const updateNotification = (id: string | number, updatedFields: Partial<ActiveNotification>) => {
    const notificationRef = notifications.value.find((n) => n.value.id === id);
    if (notificationRef) Object.assign(notificationRef.value, updatedFields);
  };

  const promiseNotification = async <T, E>(
    promise: Promise<T>,
    notification: Notification,
    successNotification?: (result: T) => Partial<ActiveNotification>,
    errorNotification?: (error: E) => Partial<ActiveNotification>,
  ): Promise<T> => {
    const id = notification.id ?? `promise-notification-${Date.now()}`;
    addNotification({ ...notification, id });
    return promise
      .then((result) => {
        if (successNotification) updateNotification(id, successNotification(result));
        else updateNotification(id, { open: false });
        return result;
      })
      .catch((error) => {
        if (errorNotification) updateNotification(id, errorNotification(error));
        else updateNotification(id, { open: false });
        throw error;
      });
  };

  return { notifications, addNotification, updateNotification, promiseNotification };
});

export const useNotifications = () => usePiniaRefStore(useNotificationStore());
